import { getIfInvoiceNumberExistsAsync, getValidateInvoiceNumberSyntax } from "../services/apiHelper";
import { purchaseOrderStatus } from "./purchaseOrderStatus";
import {
	alreadyAllocatedCustomerOrdersList,
	blockManualScanStatusList,
	closedStatusList,
	receiveStatus,
} from "./receiveStatus";
import { isLineItemCancelled, isLineItemOverReceived } from "./lineItemUtil";
import { receiveType as receiveTypes } from "./receiveTypeMapping";
import { getAvailabileForCustomerOrderQty } from "./customerOrderUtil";
import { getTimestampInLocalTime } from "./dateUtil";
import { statusToLabel } from "./statusMapping";

/**
 * Checks if there is a locked receive within the list of receives for the purchase order.
 * A receive is considered "locked" if its status matches specific criteria, or a receive exists
 * with a matching status, and is modified by a different staff member.
 * @param {Array} receives - An array of receives for the purchase order to search within.
 * @param {string | number} staffId - The identifier of the staff member performing the check.
 * @param {string} status - The status to identify which locked receives should be checked
 * @return {Object | undefined} The locked receive, otherwise undefined.
 */
export function containsLockedReceiveForStatus(receives, staffId, status) {
	return receives.find((r) => {
		switch (status) {
			case receiveStatus.inProgress:
				// The receive is locked if it's in review, or if it's in progress, and it's not modified by the staff member.
				return (
					r.status === receiveStatus.reviewing || (r.status === receiveStatus.inProgress && r.modifiedById !== staffId)
				);
			case receiveStatus.reviewing:
				// The receive is locked if it's in progress, or if it's being reviewed by another staff member.
				return (
					r.status === receiveStatus.inProgress || (r.status === receiveStatus.reviewing && r.modifiedById !== staffId)
				);
			default:
				return false;
		}
	});
}

/**
 * @param {PurchaseOrderLineItem[]} lineItems - Purchase order line items
 * @param {string} scannedProductId - The product identifier scanned by the PDA
 * @param {AdditionalProductDetails[]} products - Product inventory
 * @returns {PurchaseOrderLineItem | null} - The purchase order line item that matches the scanned product identifier
 */
export function getScannedItemByProductId(lineItems, scannedProductId, products) {
	const matchingProductWithBarcode = products.find((p) => {
		const productBarcodes = p.supplierProductDetails
			.map((d) => d.ean13.replace(/^0+/, ""))
			.concat(p.supplierProductDetails.map((d) => d.supplierBarcode.replace(/^0+/, "")))
			.concat(p.ean13.replace(/^0+/, ""))
			.filter((barcode) => barcode !== "");
		return productBarcodes.find((b) => b === scannedProductId.replace(/^0+/, ""));
	});
	let lineItem = null;
	if (matchingProductWithBarcode) {
		lineItem = lineItems.find((item) => item.sku === (matchingProductWithBarcode.id ?? matchingProductWithBarcode.sku));
	}
	if (!lineItem) {
		lineItem = lineItems.find((item) => item.model === scannedProductId);
		if (!lineItem) {
			lineItem = lineItems.find((item) => item.sku === scannedProductId);
		}
	}
	return lineItem;
}

/**
 * Used by manual receiving pages, to update one of the receives in the list of receives.
 * E.g. when user edits a line item to change the cost, this method is used to update the receive with the new cost.
 * The return from this method is a list of Receives, which the manual pages will use to update their state.
 *
 * The scenario when a receive is added is when the user has started a new in progress receive and hasn't submitted it.
 * In this case, the in progress receive is added to the list of receives.
 * @param {Receive[]} receives
 * @param {Receive} newReceive
 * @returns {Receive[]}
 */
export function addOrUpdateReceiveToReceivesList(receives, newReceive) {
	const index = receives.findIndex((r) => r.id === newReceive.id);
	return index >= 0
		? [...receives.slice(0, index), newReceive, ...receives.slice(index + 1)]
		: [...receives, newReceive];
}

function getOpenReceivesTotalReceivedQuantityBySku(receives, sku) {
	return receives.reduce((acc, r) => {
		if (!closedStatusList.includes(r.status)) {
			const lineReceivedQty = r.receiveLineItems.find((rli) => rli.sku === sku)?.receivedQuantity ?? 0;
			return acc + lineReceivedQty;
		}
		return acc;
	}, 0);
}

export function isPurchaseOrderLocked(receives) {
	return receives.some((r) => r.status === receiveStatus.inProgress || r.status === receiveStatus.reviewing);
}

export function isEditable(receive) {
	switch (receive?.status) {
		case receiveStatus.completed:
		case receiveStatus.cancelled:
		case receiveStatus.reviewing:
		case receiveStatus.receivedDirectly:
			return false;
		default:
			return true;
	}
}

export function isFullyReceived(receive) {
	return receive?.receiveLineItems.every((ri) => ri.invoicedQuantity === ri.receivedQuantity) ?? false;
}

export function isReceivedDirectInHost(status, invoiceStatus) {
	return invoiceStatus === "Closed" && status === receiveStatus.inProgress;
}

/**
 * @param {PurchaseOrder} purchaseOrder - The purchase order
 * @param {Receive[]} receives - List of Receives for this purchase order
 * @param {AdditionalProductDetails[]} productDetails - Additional product details
 * @param {InventoryDetail[]?} inventory - Product inventory
 * @returns {AdditionalPurchaseOrderLineItemDetails[]} - The mapped Receive with additional details on the line items
 */
export function mapPurchaseOrderLineItemsWithAdditionalDetails(purchaseOrder, receives, productDetails, inventory) {
	return purchaseOrder.lineItems.map((lineItem) => {
		const openTotalReceivedQuantity = getOpenReceivesTotalReceivedQuantityBySku(receives, lineItem.sku);
		const closedTotalReceivedQuantity = lineItem.receivedQuantity;
		const lineDetails = productDetails.find((p) => p.sku === lineItem.sku);

		return {
			productGroup: lineDetails?.productGroup,
			ticketPrice: lineDetails?.ticketPrice,
			promoPrice: lineDetails?.promoPrice,
			artist: lineDetails?.artist,
			format: lineDetails?.format,
			model: lineItem.model,
			barcode: lineItem.barcode,
			sku: lineItem.sku,
			description: lineItem.description,
			orderedQuantity: lineItem.quantity,
			originalQuantity: lineItem.originalQuantity,
			receivedQuantity: openTotalReceivedQuantity + closedTotalReceivedQuantity,
			originalReceivedQuantity: openTotalReceivedQuantity,
			remainder: lineItem.quantity - openTotalReceivedQuantity,
			status: lineItem.status,
			amount: lineItem.unitCostInc * lineItem.quantity,
			purchaseOrderUnitCostInc: lineItem.unitCostInc,
			purchaseOrderUnitCostEx: lineItem.unitCostEx,
			purchaseOrderLineTotalCostInc: lineItem.lineTotalCostInc,
			purchaseOrderLineTotalCostEx: lineItem.lineTotalCostEx,
			hasLabel: lineDetails?.hasLabel,
			inventory: inventory?.find((m) => m.sku === lineItem.sku) ?? { quantity: 0 },
		};
	});
}

/**
 * @param {Receive | undefined} receive - The Receive in progress
 * @returns {boolean} true if the Receive has previously been submitted, and had customer orders allocated to it
 */
export function hasReceiveAlreadyBeenAllocatedCustomerOrders(receive) {
	return receive?.statusHistory?.some((r) => alreadyAllocatedCustomerOrdersList.includes(r.status)) ?? false;
}

export function mapReceivesWithAdditionalDetails(receives, purchaseOrder, productDetails, originalReceives?, inventory?) {
	return receives.map((receive) => enrichReceiveForManualReceive(receive, purchaseOrder, productDetails, originalReceives ?? receives, inventory));
}

/**
 * @param {Receive | undefined} receive - The existing in-progress receive
 * @param {PurchaseOrder} purchaseOrder - The purchase order
 * @param {AdditionalProductDetails[]} productDetails - Additional product details
 * @param {Receive[]} originalReceives - List of Receives for this purchase order
 * @param {InventoryDetail[] | undefined} inventory - Product inventory
 * @returns {ManualReceiveDetailsProps} - The mapped Receive with additional details on the line items
 */
export function enrichReceiveForManualReceive(receive, purchaseOrder, productDetails, originalReceives, inventory) {
	if (!receive) return undefined;

	const originalReceive = originalReceives?.find((r) => r.id === receive.id);

	const purchaseOrderComment = {
		id: purchaseOrder.id ?? "",
		receiveId: receive.id,
		createdTimestamp: getTimestampInLocalTime(purchaseOrder.dateCreated),
		createdByUserId: "",
		createdByUserName: "INTEGRATION JB API",
		text: purchaseOrder.description,
		sourceSystem: "Purchase Order",
	};

	const newLineItems = receive.receiveLineItems.map((rli) => {
		const purchaseOrderLineItem = purchaseOrder.lineItems?.find((pli) => pli.sku === rli.sku);
		const lineDetails = productDetails?.find((p) => p.sku === rli.sku);
		const originalReceivedQuantity =
			originalReceive?.receiveLineItems.find((li) => li.sku === rli.sku)?.receivedQuantity ?? 0;
		let customerOrderAllocatedQty = 0;

		if (!hasReceiveAlreadyBeenAllocatedCustomerOrders(receive)) {
			const customerOrderQty = inventory?.find((m) => m.sku === rli.sku)?.quantity ?? 0;
			customerOrderAllocatedQty = getAvailabileForCustomerOrderQty(
				purchaseOrderLineItem ? purchaseOrderLineItem.quantity : 0,
				originalReceivedQuantity,
				customerOrderQty
			);
		}

		return {
			...rli,
			purchaseOrderItem: purchaseOrderLineItem,
			orderedQuantity: purchaseOrderLineItem ? purchaseOrderLineItem.quantity : 0,
			originalQuantity: purchaseOrderLineItem ? purchaseOrderLineItem.originalQuantity : 0,
			purchaseOrderReceivedQuantity: purchaseOrderLineItem ? purchaseOrderLineItem.receivedQuantity : 0,
			productGroup: lineDetails?.productGroup,
			ticketPrice: lineDetails?.ticketPrice,
			promoPrice: lineDetails?.promoPrice,
			artist: lineDetails?.artist,
			format: lineDetails?.format,
			purchaseOrderUnitCostInc: purchaseOrderLineItem ? purchaseOrderLineItem.unitCostInc : 0,
			purchaseOrderUnitCostEx: purchaseOrderLineItem ? purchaseOrderLineItem.unitCostEx : 0,
			purchaseOrderLineTotalCostInc: purchaseOrderLineItem ? purchaseOrderLineItem.lineTotalCostInc : 0,
			purchaseOrderLineTotalCostEx: purchaseOrderLineItem ? purchaseOrderLineItem.lineTotalCostEx : 0,
			customerOrderAllocatedQty,
			taxPercentage: purchaseOrderLineItem ? purchaseOrderLineItem.taxPercentage : 0,
			hasLabel: lineDetails?.hasLabel,
			inventory: inventory?.find((m) => m.sku === rli.sku) ?? { quantity: 0 },
		};
	});
	const totalOrderedQuantity = newLineItems.reduce((t, { orderedQuantity }) => t + (orderedQuantity || 0), 0);
	return {
		...receive,
		totalInvoicedQuantity: receive.receiveLineItems.reduce((t, { invoicedQuantity }) => t + invoicedQuantity, 0),
		totalReceivedQuantity: receive.receiveLineItems.reduce((t, { receivedQuantity }) => t + receivedQuantity, 0),
		totalOrderedQuantity,
		totalCostEx: receive.receiveLineItems.reduce((t, { lineTotalCostEx }) => t + lineTotalCostEx, 0),
		totalCostInc:
			receive.receiveLineItems.reduce((t, { lineTotalCostInc }) => t + lineTotalCostInc, 0) +
			(receive.totalInvoicedFreightInc ?? 0),
		receiveLineItems: newLineItems,
		comments: receive.comments ? receive.comments.concat([purchaseOrderComment]) : [purchaseOrderComment],
	};
}

/**
 * Validate the invoice number on the Receive is unique, and a valid invoice number.
 * @param {string} invoiceNumber
 * @param {string} purchaseOrderNumber
 * @param {string} vendorId
 * @param {ReceiveType | string} receiveType
 * @param {string | undefined} receiveId
 * @param {Receive[] | undefined} receives
 * @returns {Promise<{isValid: boolean, reason: string, isWarning: boolean}>}
 */
export async function validateInvoiceNumberAsync(
	invoiceNumber,
	purchaseOrderNumber,
	vendorId,
	receiveType,
	receiveId,
	receives
) {
	if (!invoiceNumber) {
		return { isValid: true, reason: "", isWarning: false };
	}

	if (invoiceNumber?.length > 20) {
		return {
			isValid: false,
			isWarning: false,
			reason: `Invoice number cannot be longer than 20 characters. ${
				receiveType === receiveTypes.edi || receiveType === receiveTypes.wasEdiLikeAManual
					? "This is likely an error by the supplier."
					: "Please check the invoice and try again."
			}`,
		};
	}
	if (invoiceNumber === purchaseOrderNumber) {
		return {
			isValid: false,
			isWarning: false,
			reason: `Invoice number cannot equal the purchase order number. ${
				receiveType === receiveTypes.edi || receiveType === receiveTypes.wasEdiLikeAManual
					? "This is likely an error by the supplier."
					: "Please check the invoice and try again."
			}`,
		};
	}
	if (invoiceNumber?.includes("#")) {
		return {
			isValid: false,
			isWarning: false,
			reason: "Invoice numbers do not contain hashs (#) please remove and try again.",
		};
	}
	if (
		receives?.some(
			(r) => r.invoiceNumber === invoiceNumber && r.id !== receiveId && r.status !== receiveStatus.cancelled
		)
	) {
		return {
			isValid: false,
			isWarning: false,
			reason: "Invoice number is aready on another receive. Please edit the existing receive if not finalised.",
		};
	}
	if (invoiceNumber?.includes("NOINVOICE")) {
		return {
			isValid: false,
			isWarning: false,
			reason:
				"NOINVOICE is not a valid invoice number. Please check the invoice copy, or log purchase order to Inventory Operations for investigation.",
		};
	}
	if (invoiceNumber?.length > 0) {
		try {
			// Skip "already exists" check for EDI. The user is receiving against an existing invoice
			if (receiveType !== receiveTypes.edi && receiveType !== receiveTypes.wasEdiLikeAManual) {
				const response = await getIfInvoiceNumberExistsAsync(invoiceNumber, vendorId);
				if (response.data) {
					return {
						isValid: false,
						isWarning: false,
						reason: `Invoice number ${invoiceNumber} has already been used for purchase order ${
							response.data.purchaseOrderNumber
						} at location ${response.data.locationId}.`,
					};
				}
			}
			if (receiveType === receiveTypes.ediLikeAManual || receiveType === receiveTypes.manual) {
				const result = await getValidateInvoiceNumberSyntax(vendorId, invoiceNumber) as any;
				const type = result?.type?.toLowerCase();
				if (type && type !== "valid") {
					return {
						isValid: type !== "force",
						reason: result.message,
						isWarning: type === "warn",
					};
				}
			}
		} catch {
			return {
				isValid: false,
				isWarning: false,
				reason: `Error validating the invoice number ${invoiceNumber}. Please try again.`,
			};
		}
	}
	return { isValid: true, reason: "", isWarning: false };
}

/**
 * Validate that the Receive is valid to submit
 * @param {{
 *     purchaseOrderItem: PurchaseOrderLineItem | undefined,
 *     sku: string,
 *     purchaseOrderReceivedQuantity: number | undefined,
 *     orderedQuantity: number | undefined,
 *     receivedQuantity: number | undefined,
 *     purchaseOrderReceivedQuantity: number | undefined,
 *     invoiced: number | undefined,
 *     quantity: number | undefined,
 *     received: number | undefined,
 *     isExtraFromReceive: boolean | undefined
 * }[]} invoiceItems
 * @param {string | undefined} receivedDate
 * @param {Receive | undefined} receive
 * @param {Receive[]} receives
 * @param {string | undefined} vendorId
 * @param {boolean | undefined} toBeFinalised
 * @returns {Promise<{isValid: boolean, reasons: string[]}>}
 */
export async function validateReceiveAsync(invoiceItems, receivedDate, receive, receives = [], vendorId, toBeFinalised = false) {
	let cancelledLines = [];
	let overReceivedLines = [];
	invoiceItems &&
		invoiceItems.forEach((item) => {
			if (isLineItemCancelled(item.purchaseOrderItem)) {
				cancelledLines.push(item.sku);
			} else if (isLineItemOverReceived(item)) {
				overReceivedLines.push(item.sku);
			}
		});
	let validationResult : { isValid: boolean, reasons: string[] } = { isValid: true, reasons: [] };
	if (receive) {
		if (receive.receiveType === receiveTypes.ediLikeAManual || receive.receiveType === receiveTypes.manual) {
			const invoiceNumberValidationResult = await validateInvoiceNumberAsync(
				receive.invoiceNumber,
				receive.purchaseOrderNumber,
				vendorId,
				receive.receiveType,
				receive.id,
				receives
			);
			if (!invoiceNumberValidationResult.isValid) {
				validationResult.isValid = false;
				validationResult.reasons.push(invoiceNumberValidationResult.reason);
			}
		}
		if (!receive?.invoiceNumber) {
			validationResult.isValid = false;
			validationResult.reasons.push("You must enter an invoice number before continuing.");
		}

		if (!receive?.invoiceDate) {
			validationResult.isValid = false;
			validationResult.reasons.push("You must selected an invoice date before continuing.");
		}

		if (toBeFinalised && receive.totalReceivedQuantity <= 0 && receive.receiveType !== receiveTypes.transfer) {
			validationResult.isValid = false;
			validationResult.reasons.push(
				"You cannot finalise an invoice where no items have been received. Please receive an item before continuing."
			);
		}
	}
	if (!receivedDate) {
		validationResult.isValid = false;
		validationResult.reasons.push("You must selected a delivery date before continuing.");
	}
	if (cancelledLines.length > 0) {
		validationResult.isValid = false;
		validationResult.reasons.push(
			`The following SKU(s) are not currently available for receiving. If the stock has been delivered and requires receiving, then tap on the 3 dots option to reopen the purchase order: ${cancelledLines.join(
				", "
			)}`
		);
	}
	if (overReceivedLines.length > 0) {
		validationResult.isValid = false;
		validationResult.reasons.push(
			`The following SKU/s have been over received, please adjust and resubmit. If you have genuinely received more stock, please manually create a PO and receive as per current over receive process: ${overReceivedLines.join(
				", "
			)}`
		);
	}
	if (invoiceItems.some((i) => i.isExtraFromReceive)) {
		const extraSkuList = invoiceItems.filter((i) => i.isExtraFromReceive).map((i) => i.sku);
		validationResult.isValid = false;
		validationResult.reasons.push(
			`The following lines are not part of this invoice, please remove them to continue.  ${extraSkuList.join(", ")}`
		);
	}
	return validationResult;
}

/**
 * Validate if the scan of the product is allowed.
 * @param {string | undefined} receiveId
 * @param {ReceiveType} receiveType
 * @param {PurchaseOrder} purchaseOrder
 * @param {ManualInvoiceEditableFields} invoiceFields
 * @param {boolean} isPurchaseOrderLocked
 * @param {purchaseOrderLineItem | null} purchaseOrderLineItem
 * @param {Receive[]} receives
 * @param {boolean} isFulfilmentCentre
 * @param {string} scannedInboundLocation
 * @param {string | undefined} receiveStatusValue
 * @returns {Promise<{isValid: boolean, reason: string}>}
 */
export async function validateScanAllowed(
	receiveId,
	receiveType,
	purchaseOrder,
	invoiceFields,
	isPurchaseOrderLocked,
	purchaseOrderLineItem,
	receives,
	isFulfilmentCentre,
	scannedInboundLocation,
	receiveStatusValue,
	isOutstandingEdiInvoiceReceive: boolean
) {
	let validationResult = { isValid: true, reason: "" };
	if (purchaseOrder.status === purchaseOrderStatus.completed) {
		validationResult = { isValid: false, reason: "Purchase order has been completed and cannot be received against." };
	} else if (receiveStatusValue && blockManualScanStatusList.includes(receiveStatusValue)) {
		validationResult = {
			isValid: false,
			reason: `Cannot scan items when the receive is status ${statusToLabel(receiveStatusValue)}`,
		};
	} else if (
		!invoiceFields.invoiceNumber ||
		!invoiceFields.invoiceDate ||
		(!invoiceFields.receivedDate && !isOutstandingEdiInvoiceReceive)
	) {
		validationResult = {
			isValid: false,
			reason: "Enter invoice number, invoice date and delivered date before scanning items.",
		};
	} else if (isPurchaseOrderLocked && receiveType !== receiveTypes.edi) {
		validationResult = {
			isValid: false,
			reason: "Unable to begin receiving as purchase order is locked.",
		};
	} else if (isFulfilmentCentre && !scannedInboundLocation) {
		validationResult = {
			isValid: false,
			reason: "Before receiving items, you must assign a location.",
		};
	} else if (!purchaseOrderLineItem) {
		validationResult = {
			isValid: false,
			reason: "Scanned item does not belong to this purchase order",
		};
	} else if (!receiveId) {
		// validate the invoice number the first time a line item is scanned
		validationResult = await validateInvoiceNumberAsync(
			invoiceFields.invoiceNumber,
			purchaseOrder.sourceId,
			purchaseOrder.vendorId,
			receiveType,
			receiveId,
			receives
		);
	}
	return validationResult;
}

/**
 * Returns SKUs from the invoice, that are not on the purchase order.
 * @param {EdiInvoice} ediInvoice
 * @param {PurchaseOrder} purchaseOrder
 * @returns {string[]}
 */
export function getSkusOnInvoiceNotOnPurchaseOrder(ediInvoice, purchaseOrder) {
	let skus = [];
	ediInvoice.lineItems.forEach((ediLineItem) => {
		if (!purchaseOrder.lineItems.some((li) => li.sku === ediLineItem.sku)) {
			skus.push(ediLineItem.sku);
		}
	});
	return skus;
}

/**
 * Enriches an existing Receive with details from the EDI invoice.
 * The scenario is when the user has started receiving an EDI Like a Manual Receive, and then the
 * invoice is received from the supplier.
 * @param {EdiInvoice} ediInvoice
 * @param {Receive} receive
 * @param {PurchaseOrder} purchaseOrder
 * @returns {Receive} the Receive enriched from the EDI invoice
 */
export function mapEdiInvoiceToReceive(ediInvoice, receive, purchaseOrder) {
	const newReceiveLineItems = ediInvoice.lineItems.map((ediLineItem) => {
		const receiveLine = receive.receiveLineItems.find((l) => l.sku === ediLineItem.sku);
		const purchaseOrderLineItem = purchaseOrder.lineItems.find((poline) => poline.sku === ediLineItem.sku);
		const { unitCostInc, unitCostEx, unitCostTax, lineCostEx, lineCostInc, lineCostTax, quantity } = ediLineItem;
		const lineTotalCostInc = lineCostInc;
		const lineTotalCostEx = lineCostEx;
		const lineTotalCostTax = lineCostTax;
		const unitDiscountCostInc = purchaseOrderLineItem.quantity
			? purchaseOrderLineItem.lineDiscountCostInc / purchaseOrderLineItem.quantity
			: 0;
		const newReceiveLine = {
			sku: purchaseOrderLineItem.sku,
			model: purchaseOrderLineItem.model,
			description: purchaseOrderLineItem.description,
			barcode: purchaseOrderLineItem.barcode,
			unitCostInc,
			unitCostEx,
			unitCostTax,
			unitDiscountCostInc,
			lineTotalCostInc,
			lineTotalCostEx,
			lineTotalCostTax,
			lineDiscountCostInc: purchaseOrderLineItem.lineDiscountCostInc,
			isDiscounted: purchaseOrderLineItem.isDiscounted,
			invoicedQuantity: quantity,
		};

		if (receiveLine) {
			return {
				...newReceiveLine,
				modifiedDate: receiveLine.modifiedDate,
				receivedQuantity: receiveLine.receivedQuantity,
			};
		} else {
			return { ...newReceiveLine, modifiedDate: new Date(), receivedQuantity: 0 };
		}
	});

	const newReceiveSkuList = newReceiveLineItems.map((item) => item.sku);
	const receiveLineNotExistWithEdiInvoice = receive.receiveLineItems.filter((r) => !newReceiveSkuList.includes(r.sku));

	const mergeReceiveLines = [...newReceiveLineItems, ...receiveLineNotExistWithEdiInvoice];

	// Check if the receive matches the invoice
	const isReceiveSameAsInvoice = () => {
		if (mergeReceiveLines.length !== ediInvoice.lineItems.length) {
			return false;
		}
		for (const invoiceLineItem of ediInvoice.lineItems) {
			const matchingLineItem = mergeReceiveLines.find(
				(li) => li.sku === invoiceLineItem.sku && li.receivedQuantity === invoiceLineItem.quantity
			);
			if (!matchingLineItem) {
				return false;
			}
		}
		return true;
	};

	return {
		...receive,
		receiveType: receiveTypes.wasEdiLikeAManual,
		invoiceDate: ediInvoice.invoiceDate,
		receiveLineItems: mergeReceiveLines,
		status: !isReceiveSameAsInvoice() ? receiveStatus.inProgress : receive.status,
	};
}

export function mapReceiveToBeFinalised(receive, graphProfile) {
	const { displayName, staffId } = graphProfile;
	return {
		...receive,
		status: receiveStatus.pendingFinalise,
		receiver: displayName,
		receiverId: staffId,
		failureReason: null,
		receiptAttemptCount: 0,
	};
}

export function mapPartReceiveToBeFinalised(receive, graphProfile) {
	const { displayName, staffId } = graphProfile;
	return {
		...receive,
		status: receiveStatus.pendingFinalise,
		receiver: displayName,
		receiverId: staffId,
		failureReason: null,
		receiptAttemptCount: 0,
		isPartReceived: true,
	};
}

/**
 * @param {Receive} receive - Receive details
 * @param {StaffProfile} graphProfile - User details
 * @returns {Receive} - Receive with calculated fields
 */
export function mapCalculatedFieldsToReceive(receive, graphProfile) {
	const { displayName, staffId } = graphProfile;
	return {
		...receive,
		totalInvoicedQuantity: receive.receiveLineItems.reduce((t, { invoicedQuantity }) => t + invoicedQuantity, 0),
		totalReceivedQuantity: receive.receiveLineItems.reduce((t, { receivedQuantity }) => t + receivedQuantity, 0),
		modifiedById: staffId,
		modifiedBy: displayName,
	};
}

/**
 * @param {{
 *     status: string,
 *     orderedQuantity: number,
 *     originalQuantity: number,
 * }[]} lineItems - Purchase order line items
 * @returns {boolean} - True, if the purchase order can be reopened
 */
export function isThereAnyPoLineAbleToBeReopened(lineItems) {
	return lineItems?.some((li) => isLineItemCancelled(li));
}

export function createReceiveLineItemFromInvoiceLineItem(item, receiveDetails) {
	const { PurchaseOrder: purchaseOrder, Invoice: invoice, MatchedReceive: receive } = receiveDetails;

	if (item.isExtraFromReceive && receive) {
		return receive.receiveLineItems.find((r) => r.sku === item.sku);
	} else {
		const purchaseOrderLineItem = purchaseOrder.lineItems.find((poline) => poline.barcode === item.barcode);
		const { lineItems: invoiceLineItems } = invoice;
		const invoiceLineItem = invoiceLineItems.find((i) => i.sku === purchaseOrderLineItem.sku);
		const { unitCostInc, unitCostEx, unitCostTax, lineCostEx, lineCostInc, lineCostTax, quantity } = invoiceLineItem;
		const unitDiscountCostInc = purchaseOrderLineItem.quantity
			? purchaseOrderLineItem.lineDiscountCostInc / purchaseOrderLineItem.quantity
			: 0;
		return {
			sku: purchaseOrderLineItem.sku,
			model: purchaseOrderLineItem.model,
			description: purchaseOrderLineItem.description,
			barcode: purchaseOrderLineItem.barcode,
			receivedQuantity: item.received,
			quantityAlreadyReceived: item.quantityAlreadyReceived ?? 0,
			originalReceivedQuantity: item.originalReceived,
			unitCostInc,
			unitCostEx,
			unitCostTax,
			unitDiscountCostInc,
			lineTotalCostInc: lineCostInc,
			lineTotalCostEx: lineCostEx,
			lineTotalCostTax: lineCostTax,
			lineDiscountCostInc: purchaseOrderLineItem.lineDiscountCostInc,
			isDiscounted: purchaseOrderLineItem.isDiscounted,
			modifiedDate: item.modifiedTimestamp,
			invoicedQuantity: quantity,
			inventory: item.inventory,
			inboundLocation: item.inboundLocation,
			hasLabel: item.hasLabel,
		};
	}
}

//Refactored from Receiving.js
export function createOrUpdateReceive(receiveDetails, invLineItems, graphProfile, receivedDate, receiveId, notes) {
	const { staffId, displayName } = graphProfile;
	const purchaseOrder = receiveDetails.PurchaseOrder;
	const invoice = receiveDetails.Invoice;
	//if receiveDetails.MatchedReceive has a value (not null or undefined). Then this is an update
	let receive = receiveDetails.MatchedReceive;

	const receiveLineItems = invLineItems.map((item) => createReceiveLineItemFromInvoiceLineItem(item, receiveDetails));

	if (receive) {
		receive.receivedDate = receivedDate;
		receive.totalReceivedQuantity = receiveLineItems.reduce((t, { receivedQuantity }) => t + receivedQuantity, 0);
		receive.modifiedBy = displayName;
		receive.modifiedById = staffId;
		receive.receiveLineItems = receiveLineItems;
		receive.notes = notes;
		if (receive.status === receiveStatus.arrived || receive.status === receiveStatus.open)
			receive.status = receiveStatus.inProgress;
	} else {
		// new receive
		receive = {
			id: receiveId,
			supplierId: purchaseOrder.vendorId,
			supplierName: purchaseOrder.vendorName,
			purchaseOrderNumber: purchaseOrder.sourceId,
			locationId: purchaseOrder.shipToLocation.locationId,
			invoiceNumber: invoice.invoiceNumber,
			organisationId: "JBH",
			status: receiveStatus.inProgress,
			receiveType: receiveTypes.edi,
			receivedDate: receivedDate,
			invoiceDate: invoice.invoiceDate,
			createdTimestamp: new Date(),
			totalInvoicedQuantity: invoice.totalQuantity,
			totalReceivedQuantity: receiveLineItems.reduce((t, { receivedQuantity }) => t + receivedQuantity, 0),
			totalInvoicedFreightEx: invoice.freightCostEx,
			totalInvoicedFreightInc: invoice.freightCostInc,
			totalInvoicedFreightTax: invoice.freightCostTax,
			isEdi: purchaseOrder.isEdi,
			modifiedBy: displayName,
			modifiedById: staffId,
			createdBy: displayName,
			createdById: staffId,
			receiveLineItems: receiveLineItems,
			notes: notes,
		};
	}

	return receive;
}

export function createReceiveFromEdiOutstanding(invoice, purchaseOrder, graphProfile) {
	const receiveLineItems = invoice.lineItems.map((item) =>
		createReceiveLineItemFromOutstandingEdiInvoiceLineItem(item, purchaseOrder)
	);

	let receive = {
		supplierId: invoice.vendorId,
		supplierName: invoice.vendorName,
		purchaseOrderNumber: invoice.purchaseOrderNumber,
		locationId: invoice.locationId,
		invoiceNumber: invoice.invoiceNumber,
		organisationId: "JBH",
		status: receiveStatus.inProgress,
		receiveType: receiveTypes.edi,
		invoiceDate: invoice.invoiceDate,
		createdTimestamp: new Date(),
		totalInvoicedQuantity: invoice.totalQuantity,
		totalReceivedQuantity: receiveLineItems.reduce((t, { receivedQuantity }) => t + receivedQuantity, 0),
		totalInvoicedFreightEx: invoice.freightCostEx,
		totalInvoicedFreightInc: invoice.freightCostInc,
		totalInvoicedFreightTax: invoice.freightCostTax,
		isEdi: true,
		modifiedBy: graphProfile.displayName,
		modifiedById: graphProfile.staffId,
		createdBy: graphProfile.displayName,
		createdById: graphProfile.staffId,
		receiveLineItems: receiveLineItems,
		notes: "Generated from Outstanding EDI Invoice count",
	};

	return receive;
}

export function createReceiveLineItemFromOutstandingEdiInvoiceLineItem(invoiceLineItem, purchaseOrder) {
	const purchaseOrderLineItem = purchaseOrder.lineItems.find((poline) => poline.barcode === invoiceLineItem.barcode);
	const {
		sku,
		model,
		description,
		barcode,
		unitCostInc,
		unitCostEx,
		unitCostTax,
		lineCostEx,
		lineCostInc,
		lineCostTax,
		quantity,
	} = invoiceLineItem;
	const unitDiscountCostInc = purchaseOrderLineItem?.quantity
		? purchaseOrderLineItem?.lineDiscountCostInc / purchaseOrderLineItem?.quantity
		: 0;
	return {
		sku,
		model,
		description,
		barcode,
		receivedQuantity: quantity,
		quantityAlreadyReceived: 0,
		originalReceivedQuantity: 0,
		unitCostInc,
		unitCostEx,
		unitCostTax,
		unitDiscountCostInc,
		lineTotalCostInc: lineCostInc,
		lineTotalCostEx: lineCostEx,
		lineTotalCostTax: lineCostTax,
		lineDiscountCostInc: unitDiscountCostInc * quantity,
		isDiscounted: purchaseOrderLineItem?.isDiscounted ?? false,
		modifiedDate: new Date(),
		invoicedQuantity: quantity,
	};
}
